import { ChevronDown, ChevronUp, Home, Pause, Play, RotateCcw, RotateCw, SkipBack, SkipForward, X } from "lucide-react";
import { formatTime } from "../../utils/formatTime";
import { SpeedControl } from "./SpeedControl";
import { SponsorBanner, SPONSOR_IMAGES } from "./SponsorBanner";

const sponsorImageForChapter = (chapterId) => {
  const key = String(chapterId ?? "");
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) % SPONSOR_IMAGES.length;
  return SPONSOR_IMAGES[hash % SPONSOR_IMAGES.length];
};

const SkipButton = ({ direction, seconds, onSkip }) => {
  const Icon = direction === "back" ? RotateCcw : RotateCw;
  return (
    <button
      onClick={() => onSkip(direction === "back" ? -seconds : seconds)}
      aria-label={direction === "back" ? `Retroceder ${seconds} segundos` : `Adelantar ${seconds} segundos`}
      className="relative w-10 h-10 flex items-center justify-center text-white"
    >
      <Icon size={36} strokeWidth={1.6} />
      <span className="absolute text-[10px] font-bold leading-none">{seconds}</span>
    </button>
  );
};

export const PlayerScreen = ({
  track,
  tracks,
  chapters,
  currentIndex,
  currentTime,
  duration,
  isPlaying,
  playbackRate,
  onClose,
  onTogglePlay,
  onSeek,
  onSkip,
  onPrev,
  onNext,
  onSetRate,
  onJump,
  onSwitchType,
  onMoveTrack,
  onRemoveTrack,
}) => {
  const progressRatio = duration > 0 ? currentTime / duration : 0;
  const chapter = chapters.find((c) => c.id === track.chapterId);
  const hasBothVariants = Boolean(chapter?.variants.resumen && chapter?.variants.completo);
  const sponsorImage = sponsorImageForChapter(chapter?.id ?? track.chapterId);

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-[#160e2f]">
      <div
        className="sticky z-20 pt-1 pb-2 px-6"
        style={{
          top: "var(--podcast-header-h, 160px)",
          marginTop: "var(--podcast-header-h, 160px)",
          background: "rgba(22,14,47,.97)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="max-w-md mx-auto">
          <div
            className="rounded-[24px] p-2 text-center"
            style={{ background: "linear-gradient(150deg,#1e1140,#2b0f3d 60%,#3d0f38)", border: "1px solid rgba(255,255,255,.09)" }}
          >
            <SponsorBanner />
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 pt-2 pb-16">
        <div className="mt-0 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: track.color }} />
            <span
              className="text-[11px] font-bold tracking-widest uppercase"
              style={{ color: track.color }}
            >
              Episodio {currentIndex + 1} de {tracks.length}
            </span>
          </div>

          <button
            onClick={onClose}
            title="Inicio"
            aria-label="Inicio"
            className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/25 text-white text-sm font-semibold pl-2.5 pr-3.5 h-9 shadow-lg transition-colors shrink-0"
          >
            <Home size={18} strokeWidth={2.5} />
            Inicio
          </button>
        </div>

        <div className="mt-1">
          <p className="text-2xl font-bold tracking-tight text-white truncate">{track.name}</p>

          {hasBothVariants && (
            <div className="flex items-center gap-1 rounded-full bg-white/5 p-0.5 mt-2 w-fit">
              {["resumen", "completo"].map((type) => (
                <button
                  key={type}
                  onClick={() => onSwitchType(type)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide transition-colors ${track.type === type
                    ? "bg-white text-[#160e2f]"
                    : "text-white/50 hover:text-white/80"
                    }`}
                >
                  {type === "completo" ? "Completo" : "Resumen"}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-2">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={(e) => onSeek(Number(e.target.value))}
            className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-white"
            style={{
              background: `linear-gradient(to right, ${track.color} ${progressRatio * 100}%, rgba(255,255,255,.14) ${progressRatio * 100}%)`,
            }}
          />
          <div className="flex justify-between text-xs font-mono text-white/55 mt-2">
            <span>{formatTime(currentTime)}</span>
            <span>-{formatTime(Math.max(0, duration - currentTime))}</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-2">
          <button
            onClick={onPrev}
            disabled={currentIndex === 0}
            className={currentIndex === 0 ? "opacity-20" : "text-white/80 hover:text-white transition-transform hover:scale-110"}
          >
            <SkipBack size={18} fill="currentColor" />
          </button>
          <SkipButton direction="back" seconds={10} onSkip={onSkip} />
          <button
            onClick={onTogglePlay}
            className="w-16 h-16 rounded-full bg-[#e0217a] flex items-center justify-center text-white shadow-[0_10px_30px_rgba(224,33,122,.45)]"
          >
            {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
          </button>
          <SkipButton direction="forward" seconds={10} onSkip={onSkip} />
          <button
            onClick={onNext}
            disabled={currentIndex === tracks.length - 1}
            className={currentIndex === tracks.length - 1 ? "opacity-20" : "text-white/80 hover:text-white transition-transform hover:scale-110"}
          >
            <SkipForward size={18} fill="currentColor" />
          </button>
        </div>

        <p className="text-xs font-semibold text-white/55 mt-2">Velocidad</p>
        <div className="mt-2">
          <SpeedControl rate={playbackRate} onChange={onSetRate} />
        </div>

        <div className="flex items-center justify-between mt-2">
          <p className="text-sm font-bold text-white">En reproducción</p>
          <span className="text-[11px] text-white/40">Reordena con las flechas</span>
        </div>
        <div className="flex flex-col gap-1 mt-2">
          {tracks.map((item, index) => {
            const isActive = index === currentIndex;
            return (
              <div
                key={`${item.key}-${index}`}
                onClick={() => onJump(index)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer"
                style={{ background: isActive ? "rgba(255,255,255,.09)" : "transparent" }}
              >
                <span className="font-mono text-[11px] font-bold w-5 shrink-0" style={{ color: item.color }}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{item.name}</p>
                  <p className="text-[10px] text-white/40 uppercase">
                    {item.type === "completo" ? "Completo" : "Resumen"}
                  </p>
                </div>

                <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    disabled={index === 0}
                    onClick={() => onMoveTrack(index, index - 1)}
                    className="p-1 text-white/50 disabled:opacity-20 hover:text-white"
                    title="Subir"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    disabled={index === tracks.length - 1}
                    onClick={() => onMoveTrack(index, index + 1)}
                    className="p-1 text-white/50 disabled:opacity-20 hover:text-white"
                    title="Bajar"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    disabled={tracks.length === 1}
                    onClick={() => onRemoveTrack(index)}
                    className="p-1 text-white/40 disabled:opacity-20 hover:text-red-400"
                    title="Quitar"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
